import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import appDataManager from '../../services/AppDataManager';

// Echtzeit-Listener über AppDataManager starten
let cleanupCallback = null;

export const subscribeToNotifications = ({ userId }) => (dispatch) => {
  // Bestehenden Callback aufräumen
  if (cleanupCallback) {
    cleanupCallback();
    cleanupCallback = null;
  }

  if (!userId) return;

  // Callback registrieren der Redux-State updatet wenn AppDataManager neue Daten hat
  cleanupCallback = appDataManager.registerCallback('notifications', (notifications) => {
    dispatch(notificationsSlice.actions.setNotifications(notifications));
  });

  // Listener im AppDataManager starten
  appDataManager.setupNotificationsListener(userId);
};

export const unsubscribeFromNotifications = () => () => {
  if (cleanupCallback) {
    cleanupCallback();
    cleanupCallback = null;
  }
};

// Async thunks
export const createNotification = createAsyncThunk(
  'notifications/createNotification',
  async (notificationData, { rejectWithValue }) => {
    try {
      const { userId, title, body, type, data = {} } = notificationData;

      const newNotification = {
        userId,
        title,
        body,
        type,
        read: false,
        createdAt: new Date().toISOString(),
        data
      };

      await addDoc(collection(db, 'notifications'), newNotification);
      // onSnapshot kümmert sich um das State-Update
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);

      await updateDoc(notificationRef, {
        read: true
      });

      return notificationId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async (userId, { rejectWithValue, getState }) => {
    try {
      const { notifications } = getState().notifications;
      const unreadNotifications = notifications.filter(notification =>
        !notification.read && notification.userId === userId
      );

      const updatePromises = unreadNotifications.map(notification =>
        updateDoc(doc(db, 'notifications', notification.id), {
          read: true
        })
      );

      await Promise.all(updatePromises);

      return unreadNotifications.map(notification => notification.id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteNotification = createAsyncThunk(
  'notifications/deleteNotification',
  async (notificationId, { rejectWithValue }) => {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
      return notificationId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteAllNotifications = createAsyncThunk(
  'notifications/deleteAll',
  async (userId, { rejectWithValue, getState }) => {
    try {
      const { notifications } = getState().notifications;
      const userNotifications = notifications.filter(notification =>
        notification.userId === userId
      );

      const deletePromises = userNotifications.map(notification =>
        deleteDoc(doc(db, 'notifications', notification.id))
      );

      await Promise.all(deletePromises);

      return userNotifications.map(notification => notification.id);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  notifications: [],
  unreadCount: 0,
  overdueRidesCount: 0,
  isLoading: false,
  error: null
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotificationsError: (state) => {
      state.error = null;
    },
    setNotifications: (state, action) => {
      state.isLoading = false;
      state.error = null;
      state.notifications = action.payload;
      state.unreadCount = action.payload.filter(n => !n.read).length;
      state.overdueRidesCount = action.payload.filter(n =>
        !n.read && n.type === 'overdue_ride'
      ).length;
    }
  },
  extraReducers: (builder) => {
    builder
      // Create notification
      .addCase(createNotification.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Mark notification as read — optimistisches Update
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const notification = state.notifications.find(n => n.id === action.payload);
        if (notification && !notification.read) {
          notification.read = true;
          state.unreadCount -= 1;
          if (notification.type === 'overdue_ride') {
            state.overdueRidesCount -= 1;
          }
        }
      })
      .addCase(markNotificationAsRead.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Mark all notifications as read — optimistisches Update
      .addCase(markAllNotificationsAsRead.fulfilled, (state, action) => {
        action.payload.forEach(id => {
          const notification = state.notifications.find(n => n.id === id);
          if (notification) {
            notification.read = true;
          }
        });
        state.unreadCount = state.notifications.filter(n => !n.read).length;
        state.overdueRidesCount = state.notifications.filter(n =>
          !n.read && n.type === 'overdue_ride'
        ).length;
      })
      .addCase(markAllNotificationsAsRead.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete notification — optimistisches Update
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const deleted = state.notifications.find(n => n.id === action.payload);
        state.notifications = state.notifications.filter(n => n.id !== action.payload);
        if (deleted && !deleted.read) {
          state.unreadCount -= 1;
          if (deleted.type === 'overdue_ride') {
            state.overdueRidesCount -= 1;
          }
        }
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Delete all notifications
      .addCase(deleteAllNotifications.fulfilled, (state, action) => {
        state.notifications = state.notifications.filter(
          n => !action.payload.includes(n.id)
        );
        state.unreadCount = state.notifications.filter(n => !n.read).length;
        state.overdueRidesCount = state.notifications.filter(n =>
          !n.read && n.type === 'overdue_ride'
        ).length;
      })
      .addCase(deleteAllNotifications.rejected, (state, action) => {
        state.error = action.payload;
      });
  }
});

export const { clearNotificationsError } = notificationsSlice.actions;
export default notificationsSlice.reducer;
